import { useState, useMemo, useEffect } from 'react'
import _ from 'lodash'
import { useMutation } from '@apollo/client/react'
import {
  setNotificationAsVisited,
  getRecentActivities,
  triggerDownload,
  navigateToNotificationUrl,
  getNotificationMessageByStatus,
  getGroupedAndSingleActivities,
} from '../utils/backgroundNotificationsHelper'
import { UPDATE_BACKGROUND_NOTIFICATION } from 'graphql/mutations/backgroundActivity/backgroundActivityMutations'
import { useToastAlert } from '@design-systems/canvas-ui'
import { useNavigate } from 'react-router-dom'
import type { BackgroundActivityNotification } from '../types/BackgroundActivityTypes'

type ToastLink = { text: string; onClick: () => void }

type ToastItem = BackgroundActivityNotification & {
  isMultiple?: boolean
  key?: string
  count?: number
  activities?: BackgroundActivityNotification[]
}

export enum BACKGROUND_NOTIFICATIONS_STATUS {
  INPROGRESS = 'INPROGRESS',
  ERROR = 'ERROR',
  COMPLETED = 'COMPLETED',
}

export enum BACKGROUND_NOTIFICATIONS_ACTIONS {
  REDIRECT = 'REDIRECT',
  DOWNLOAD = 'DOWNLOAD',
}

const toastColors: Record<
  BACKGROUND_NOTIFICATIONS_STATUS,
  'info' | 'success' | 'error'
> = {
  [BACKGROUND_NOTIFICATIONS_STATUS.INPROGRESS]: 'info',
  [BACKGROUND_NOTIFICATIONS_STATUS.COMPLETED]: 'success',
  [BACKGROUND_NOTIFICATIONS_STATUS.ERROR]: 'error',
}

type Props = {
  backgroundActivities: BackgroundActivityNotification[]
  generatedTime: string
  notificationRef: any
}

const BackgroundActivityToasters = (props: Props) => {
  const { backgroundActivities, notificationRef } = props

  // Snapshot once at mount so state transitions on activities created during
  // this session still pass isRecent — generatedTime advances every poll and
  // would otherwise filter out later COMPLETED/ERROR transitions.
  const [sessionStart] = useState(() => new Date().toISOString())

  const { singleActivities, groupedActivities } = useMemo(
    () => getGroupedAndSingleActivities(backgroundActivities, sessionStart),
    [backgroundActivities, sessionStart],
  )

  const navigate = useNavigate()
  const [updateBackgroundNotification] = useMutation(
    UPDATE_BACKGROUND_NOTIFICATION,
  )

  const makeToast = useToastAlert()

  const [expandedToasters, setExpandedToaster] = useState<string[]>([])

  const orderedToasters = useMemo<ToastItem[]>(() => {
    const unorderedToastersList = getRecentActivities(
      singleActivities,
      sessionStart,
    )

    const inProgressToasts = _.filter(unorderedToastersList, {
      status: BACKGROUND_NOTIFICATIONS_STATUS.INPROGRESS,
    })
    const completedToasts = _.filter(unorderedToastersList, {
      status: BACKGROUND_NOTIFICATIONS_STATUS.COMPLETED,
    })
    const errorToasts = _.filter(unorderedToastersList, {
      status: BACKGROUND_NOTIFICATIONS_STATUS.ERROR,
    })

    const collapsedGroups = groupedActivities.filter(
      (group) => !expandedToasters.includes(group.key),
    )
    const toasters: ToastItem[] = [
      ...inProgressToasts,
      ...completedToasts,
      ...errorToasts,
      ...collapsedGroups,
    ]

    groupedActivities.forEach((group) => {
      if (expandedToasters.includes(group.key)) {
        toasters.push(...group.activities)
      }
    })
    return toasters
  }, [expandedToasters, groupedActivities, sessionStart, singleActivities])

  useEffect(() => {
    orderedToasters.forEach((toast) => {
      const toastIdentifier = `${toast.id}${toast.status}`
      if (notificationRef?.current?.includes(toastIdentifier)) return
      notificationRef?.current?.push(toastIdentifier)
      let notificationMessage
      if (!toast.isMultiple) {
        notificationMessage = getNotificationMessageByStatus(
          toast,
          toast.status,
        )
      } else {
        notificationMessage = toast.notificationMessageList[0]
      }
      let approveButtonText: string | null = null
      let onRefuseToast: () => void = () => {}
      let onApproveToast: () => void = () => {}
      if (toast.isMultiple) {
        onRefuseToast = () =>
          setExpandedToaster([...expandedToasters, toast.key as string])
      } else {
        switch (toast.status) {
          case BACKGROUND_NOTIFICATIONS_STATUS.INPROGRESS:
            onRefuseToast = () => {}
            break
          case BACKGROUND_NOTIFICATIONS_STATUS.COMPLETED:
            approveButtonText =
              toast.action === BACKGROUND_NOTIFICATIONS_ACTIONS.DOWNLOAD
                ? 'Download'
                : 'View'
            onRefuseToast = () => {
              setNotificationAsVisited(toast, updateBackgroundNotification)
            }
            onApproveToast = () => {
              switch (toast.action) {
                case BACKGROUND_NOTIFICATIONS_ACTIONS.DOWNLOAD:
                  setNotificationAsVisited(toast, updateBackgroundNotification)
                  triggerDownload(toast)
                  break
                case BACKGROUND_NOTIFICATIONS_ACTIONS.REDIRECT:
                  setNotificationAsVisited(toast, updateBackgroundNotification)
                  navigateToNotificationUrl(toast, navigate)
                  break
                default:
                  break
              }
            }
            break
          case BACKGROUND_NOTIFICATIONS_STATUS.ERROR:
            onRefuseToast = () => {
              setNotificationAsVisited(toast, updateBackgroundNotification)
            }
            break
          default:
            break
        }
      }

      const isCompleted = toast.status === 'COMPLETED'

      const toastConfig = {
        variant: toastColors[toast.status as BACKGROUND_NOTIFICATIONS_STATUS],
        heading: notificationMessage?.title ?? '',
        message: notificationMessage?.comment,
        links: [] as ToastLink[],
        key: `${toast.id}${toast.status}`,
        index: `${toast.id}${toast.status}`,
        'data-testid': `toast-${toast.id}${toast.status}`,
        autoHideDuration: isCompleted ? 10000 : 5000,
        isPauseOnHover: true,
        onComplete: () => {
          if (isCompleted) {
            if (toast.activities && toast.activities.length) {
              toast.activities.forEach(
                (activity: BackgroundActivityNotification) => {
                  setNotificationAsVisited(
                    activity,
                    updateBackgroundNotification,
                  )
                },
              )
            } else {
              setNotificationAsVisited(toast, updateBackgroundNotification)
            }
          }
        },
      }
      if (approveButtonText) {
        toastConfig.links.push({
          text: approveButtonText,
          onClick: onApproveToast,
        })
      }
      if (toast.isMultiple) {
        toastConfig.links.push({ text: 'Show all', onClick: onRefuseToast })
      }
      makeToast(toastConfig)
    })
  }, [
    expandedToasters,
    navigate,
    makeToast,
    orderedToasters,
    notificationRef,
    updateBackgroundNotification,
  ])

  return null
}

export default BackgroundActivityToasters
